import { Link, useLocation } from '@tanstack/react-router';
import { useEffect, useMemo, useRef, useState } from 'react';
import { activeNavTo, type NavGroup } from './UserMenu.js';

/**
 * Shared collapsible left-nav for AuthedLayout + EmployeeLayout.
 *
 * Why collapsible: the admin sidebar has eight groups / ~30 links; rendered
 * fully expanded it was a long scroll ("scrolling to the bottom was quite a
 * journey" — external review). Groups are now accordion sections:
 *
 *   - On first load, only the group containing the active route is expanded
 *     (auto-align to the user's current section); the rest are collapsed.
 *   - Per-group open/closed state is remembered in localStorage (keyed by
 *     `storageKey` so the admin and employee navs don't collide), so a user's
 *     manual expand/collapse choices survive navigation and reload.
 *   - Navigating into a section that the user hasn't explicitly collapsed
 *     auto-reveals it, so the current page's siblings are always reachable
 *     without a click.
 *
 * Collapsed groups are unmounted (not just visually hidden) so screen readers
 * and tab order skip them, matching the visual affordance.
 */
export function NavSidebar({
  groups,
  ariaLabel,
  storageKey,
}: {
  groups: NavGroup[];
  ariaLabel: string;
  storageKey: string;
}): JSX.Element {
  const location = useLocation();
  const best = activeNavTo(location.pathname, groups);
  const activeGroupLabel = useMemo(
    () => groups.find((g) => g.items.some((i) => i.to === best))?.label ?? null,
    [groups, best],
  );

  const [open, setOpen] = useState<Record<string, boolean>>(() =>
    initialOpenState(groups, activeGroupLabel, storageKey),
  );

  // Persist whenever the open map changes.
  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(open));
    } catch {
      // Private-mode / storage-disabled: degrade to in-memory state only.
    }
  }, [open, storageKey]);

  // Auto-reveal the active group when the user navigates into a *new* section
  // (only on change, so it doesn't fight a user who just collapsed it in place).
  const prevActive = useRef(activeGroupLabel);
  useEffect(() => {
    if (activeGroupLabel && activeGroupLabel !== prevActive.current) {
      setOpen((o) => (o[activeGroupLabel] ? o : { ...o, [activeGroupLabel]: true }));
    }
    prevActive.current = activeGroupLabel;
  }, [activeGroupLabel]);

  function toggle(label: string): void {
    setOpen((o) => ({ ...o, [label]: !o[label] }));
  }

  return (
    <nav
      aria-label={ariaLabel}
      className="w-56 shrink-0 border-r border-gray-900 py-4 flex flex-col text-sm overflow-y-auto"
    >
      {groups.map((group) => {
        const expanded = open[group.label] ?? false;
        const containsActive = group.label === activeGroupLabel;
        const regionId = `nav-group-${storageKey}-${group.label}`.replace(/\s+/g, '-');
        return (
          <div key={group.label} className="mb-1">
            <button
              type="button"
              onClick={() => toggle(group.label)}
              aria-expanded={expanded}
              aria-controls={regionId}
              className={`w-full px-5 pt-2 pb-1 flex items-center justify-between text-[10px] uppercase tracking-wider hover:text-gray-300 ${
                containsActive ? 'text-gray-300' : 'text-gray-600'
              }`}
            >
              <span>{group.label}</span>
              <span aria-hidden className="text-[9px]">
                {expanded ? '▾' : '▸'}
              </span>
            </button>
            {expanded ? (
              <div id={regionId} className="flex flex-col gap-1">
                {group.items.map((item) => {
                  const active = item.to === best;
                  const base = 'mx-2 px-3 py-2 rounded flex items-center justify-between';
                  if (item.disabled) {
                    return (
                      <span
                        key={item.to}
                        className={`${base} text-gray-600 cursor-not-allowed`}
                        title="Coming next"
                      >
                        {item.label}
                        {item.tag ? <span className="text-xs text-gray-700">{item.tag}</span> : null}
                      </span>
                    );
                  }
                  return (
                    <Link
                      key={item.to}
                      to={item.to}
                      className={`${base} ${
                        active ? 'bg-gray-900 text-white' : 'text-gray-400 hover:bg-gray-950 hover:text-white'
                      }`}
                    >
                      {item.label}
                    </Link>
                  );
                })}
              </div>
            ) : null}
          </div>
        );
      })}
    </nav>
  );
}

/**
 * First-render open map: a group is open if localStorage has an explicit
 * preference for it, otherwise open only if it's the group holding the active
 * route. Malformed/absent storage falls back to active-group-only.
 */
function initialOpenState(
  groups: NavGroup[],
  activeGroupLabel: string | null,
  storageKey: string,
): Record<string, boolean> {
  let stored: Record<string, unknown> = {};
  try {
    const raw = localStorage.getItem(storageKey);
    if (raw) stored = JSON.parse(raw) as Record<string, unknown>;
  } catch {
    stored = {};
  }
  const out: Record<string, boolean> = {};
  for (const g of groups) {
    out[g.label] =
      typeof stored[g.label] === 'boolean' ? (stored[g.label] as boolean) : g.label === activeGroupLabel;
  }
  return out;
}
